import {
  createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState,
  type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode,
  type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { X, Check, AlertCircle, Info, Loader2, ChevronDown, Search } from 'lucide-react';

export const cn = (...inputs: any[]) => twMerge(clsx(inputs));

// ── Button ──────────────────────────────────────────────────────────────────

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-brand-ink hover:brightness-95 active:brightness-90 font-semibold shadow-sm',
  secondary: 'bg-raised text-ink hover:bg-line border border-line',
  ghost: 'text-muted hover:text-ink hover:bg-raised',
  danger: 'bg-danger text-white hover:brightness-110 font-semibold',
  outline: 'border border-line text-ink hover:bg-raised hover:border-faint',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-sm gap-2 rounded-xl',
  lg: 'h-12 px-6 text-base gap-2 rounded-xl',
  icon: 'h-9 w-9 rounded-lg justify-center',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'secondary', size = 'md', loading, icon, className, children, disabled, ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap transition-all',
        'disabled:opacity-50 disabled:pointer-events-none',
        BUTTON_VARIANTS[variant], BUTTON_SIZES[size], className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : icon}
      {children}
    </button>
  );
}

// ── Card ────────────────────────────────────────────────────────────────────

export function Card({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('card', className)} {...props}>{children}</div>;
}

export function CardHeader({ title, subtitle, action, className }: {
  title: ReactNode; subtitle?: ReactNode; action?: ReactNode; className?: string;
}) {
  return (
    <div className={cn('flex items-start justify-between gap-4 p-5 sm:p-6 pb-0', className)}>
      <div className="min-w-0">
        <h2 className="text-lg font-bold tracking-tight truncate">{title}</h2>
        {subtitle && <p className="text-sm text-muted mt-0.5">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

// ── Badge ───────────────────────────────────────────────────────────────────

export type BadgeTone = 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'brand' | 'accent';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-raised text-muted border-line',
  ok: 'bg-ok/12 text-ok border-ok/25',
  warn: 'bg-warn/12 text-warn border-warn/25',
  danger: 'bg-danger/12 text-danger border-danger/25',
  info: 'bg-info/12 text-info border-info/25',
  brand: 'bg-brand/15 text-brand border-brand/30',
  accent: 'bg-accent/15 text-accent border-accent/30',
};

export function Badge({ tone = 'neutral', children, className, dot }: {
  tone?: BadgeTone; children: ReactNode; className?: string; dot?: boolean;
}) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5',
      'text-xs font-semibold whitespace-nowrap',
      BADGE_TONES[tone], className,
    )}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}

/** Maps the domain enums onto badge tones, so status colour is consistent. */
export function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'verified': case 'paid': case 'active': case 'occupied': case 'delivered':
    case 'resolved': case 'ok':
      return 'ok';
    case 'pending': case 'part_paid': case 'partial': case 'ending': case 'reserved':
    case 'in_progress': case 'acknowledged': case 'queued': case 'sent': case 'notice':
      return 'warn';
    case 'rejected': case 'overdue': case 'failed': case 'undelivered': case 'terminated':
    case 'blacklisted': case 'urgent': case 'disputed':
      return 'danger';
    case 'draft': case 'applicant': case 'vacant': case 'open': case 'unpaid':
      return 'info';
    case 'maintenance': case 'high':
      return 'accent';
    default:
      return 'neutral';
  }
}

// ── Form controls ───────────────────────────────────────────────────────────

export function Field({ label, hint, error, required, children, className }: {
  label?: string; hint?: string; error?: string; required?: boolean;
  children: ReactNode; className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label className="block text-sm font-medium text-ink">
          {label}
          {required && <span className="text-danger ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-xs text-danger flex items-center gap-1">
          <AlertCircle className="h-3 w-3 shrink-0" />{error}
        </p>
      ) : hint ? (
        <p className="text-xs text-faint">{hint}</p>
      ) : null}
    </div>
  );
}

const CONTROL = 'w-full bg-raised border border-line rounded-xl px-3 h-10 text-sm text-ink '
  + 'placeholder:text-faint transition-colors focus:border-brand focus:outline-none '
  + 'focus:ring-2 focus:ring-brand/25 disabled:opacity-50';

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(CONTROL, className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(CONTROL, 'h-auto py-2.5 min-h-[80px] resize-y', className)} {...props} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative">
      <select className={cn(CONTROL, 'appearance-none pr-9 cursor-pointer', className)} {...props}>
        {children}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-faint pointer-events-none" />
    </div>
  );
}

/** Money input that keeps cents as the source of truth. */
export function MoneyInput({ valueCents, onChangeCents, currency = 'USD', className, ...props }: {
  valueCents: number;
  onChangeCents: (cents: number) => void;
  currency?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  const [text, setText] = useState(() => (valueCents / 100).toFixed(2));
  const [focused, setFocused] = useState(false);

  // Re-sync when the parent changes the value from outside (e.g. loading a record).
  useEffect(() => {
    if (!focused) setText((valueCents / 100).toFixed(2));
  }, [valueCents, focused]);

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-faint pointer-events-none">
        {currency}
      </span>
      <input
        {...props}
        className={cn(CONTROL, 'pl-12 tnum text-right', className)}
        inputMode="decimal"
        value={text}
        onFocus={(e) => { setFocused(true); e.target.select(); }}
        onBlur={() => {
          setFocused(false);
          setText((valueCents / 100).toFixed(2));
        }}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          const cleaned = raw.replace(/[^0-9.]/g, '');
          const [whole = '0', frac = ''] = cleaned.split('.');
          onChangeCents(Number(whole || 0) * 100 + Number((frac + '00').slice(0, 2)));
        }}
      />
    </div>
  );
}

export function SearchInput({ value, onChange, placeholder = 'Search…', className }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string;
}) {
  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-faint pointer-events-none" />
      <input
        className={cn(CONTROL, 'pl-9')}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-faint hover:text-ink"
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function Checkbox({ label, description, className, ...props }: {
  label?: ReactNode; description?: string;
} & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <label htmlFor={id} className={cn('flex items-start gap-2.5 cursor-pointer group', className)}>
      <input
        id={id}
        type="checkbox"
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-line bg-raised text-brand
                   focus:ring-2 focus:ring-brand/30 cursor-pointer accent-[rgb(var(--brand))]"
        {...props}
      />
      {(label || description) && (
        <span className="min-w-0">
          {label && <span className="block text-sm text-ink group-hover:text-ink">{label}</span>}
          {description && <span className="block text-xs text-faint mt-0.5">{description}</span>}
        </span>
      )}
    </label>
  );
}

// ── Modal ───────────────────────────────────────────────────────────────────

export function Modal({ open, onClose, title, subtitle, children, footer, size = 'md' }: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Stop the page behind from scrolling while a dialog is up.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-fade-up"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'relative w-full bg-surface border border-line shadow-pop animate-scale-in',
          'rounded-t-3xl sm:rounded-2xl max-h-[92vh] flex flex-col',
          widths[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-line shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg font-bold tracking-tight">{title}</h2>
            {subtitle && <p className="text-sm text-muted mt-1">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-1.5 rounded-lg text-faint hover:text-ink hover:bg-raised transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 sm:p-6 overflow-y-auto flex-1">{children}</div>

        {footer && (
          <div className="p-5 sm:p-6 border-t border-line flex items-center justify-end gap-3 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Confirmation for destructive or irreversible actions. */
export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', tone = 'danger', loading }: {
  open: boolean; onClose: () => void; onConfirm: () => void;
  title: string; message: ReactNode; confirmLabel?: string;
  tone?: 'danger' | 'primary'; loading?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant={tone} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <div className="text-sm text-muted leading-relaxed">{message}</div>
    </Modal>
  );
}

// ── Toast ───────────────────────────────────────────────────────────────────

interface Toast { id: number; message: string; tone: 'ok' | 'danger' | 'info' }
interface ToastApi {
  success: (m: string) => void;
  error: (m: string) => void;
  info: (m: string) => void;
}

const ToastContext = createContext<ToastApi>({
  success: () => {}, error: () => {}, info: () => {},
});

export const useToast = () => useContext(ToastContext);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(0);

  const push = useCallback((message: string, tone: Toast['tone']) => {
    const id = nextId.current++;
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);

  const api = useMemo<ToastApi>(() => ({
    success: (m) => push(m, 'ok'),
    error: (m) => push(m, 'danger'),
    info: (m) => push(m, 'info'),
  }), [push]);

  const icons = {
    ok: <Check className="h-4 w-4 text-ok shrink-0" />,
    danger: <AlertCircle className="h-4 w-4 text-danger shrink-0" />,
    info: <Info className="h-4 w-4 text-info shrink-0" />,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] sm:w-auto">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className="flex items-start gap-3 bg-surface border border-line rounded-xl
                       px-4 py-3 shadow-pop animate-fade-up"
          >
            {icons[t.tone]}
            <p className="text-sm text-ink flex-1 min-w-0">{t.message}</p>
            <button
              onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}
              className="text-faint hover:text-ink shrink-0"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────

export function PageHeader({ title, subtitle, action, breadcrumb }: {
  title: ReactNode; subtitle?: ReactNode; action?: ReactNode; breadcrumb?: ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div className="min-w-0">
        {breadcrumb && <div className="mb-2">{breadcrumb}</div>}
        <h1 className="text-title">{title}</h1>
        {subtitle && <p className="text-muted mt-1.5">{subtitle}</p>}
      </div>
      {action && <div className="flex items-center gap-2 shrink-0">{action}</div>}
    </div>
  );
}

export function EmptyState({ icon, title, message, action }: {
  icon?: ReactNode; title: string; message?: string; action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      {icon && (
        <div className="h-14 w-14 rounded-2xl bg-raised border border-line
                        flex items-center justify-center text-faint mb-4">
          {icon}
        </div>
      )}
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {message && <p className="text-sm text-muted mt-1.5 max-w-sm leading-relaxed">{message}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-5 w-5 animate-spin text-muted', className)} />;
}

export function LoadingBlock({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2.5 p-1">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton h-12" />
      ))}
    </div>
  );
}

/** Horizontal scroll container for wide tables — the page must never scroll sideways. */
export function TableWrap({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('overflow-x-auto -mx-5 sm:mx-0 px-5 sm:px-0', className)}>
      <div className="min-w-full inline-block align-middle">{children}</div>
    </div>
  );
}

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return <table className={cn('w-full text-sm border-collapse', className)}>{children}</table>;
}

export function Th({ children, className, align = 'left' }: {
  children?: ReactNode; className?: string; align?: 'left' | 'right' | 'center';
}) {
  return (
    <th className={cn(
      'label py-2.5 px-3 border-b border-line font-semibold whitespace-nowrap',
      align === 'right' && 'text-right', align === 'center' && 'text-center',
      align === 'left' && 'text-left',
      className,
    )}>
      {children}
    </th>
  );
}

export function Td({ children, className, align = 'left' }: {
  children?: ReactNode; className?: string; align?: 'left' | 'right' | 'center';
}) {
  return (
    <td className={cn(
      'py-3 px-3 border-b border-line/60 align-middle',
      align === 'right' && 'text-right', align === 'center' && 'text-center',
      className,
    )}>
      {children}
    </td>
  );
}

export function Tabs({ tabs, active, onChange }: {
  tabs: { id: string; label: string; count?: number }[];
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="flex gap-1 border-b border-line overflow-x-auto -mx-1 px-1" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={active === tab.id}
          onClick={() => onChange(tab.id)}
          className={cn(
            'relative px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors',
            'border-b-2 -mb-px',
            active === tab.id
              ? 'text-ink border-brand'
              : 'text-muted border-transparent hover:text-ink',
          )}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className={cn(
              'ml-2 text-xs px-1.5 py-0.5 rounded-full tnum',
              active === tab.id ? 'bg-brand/15 text-brand' : 'bg-raised text-faint',
            )}>
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/** Key/value row used throughout detail panels. */
export function DetailRow({ label, children, className }: {
  label: string; children: ReactNode; className?: string;
}) {
  return (
    <div className={cn('flex items-baseline justify-between gap-4 py-2 border-b border-line/50 last:border-0', className)}>
      <dt className="text-sm text-muted shrink-0">{label}</dt>
      <dd className="text-sm text-ink font-medium text-right min-w-0 truncate">{children}</dd>
    </div>
  );
}

export function Avatar({ name, size = 'md', className }: {
  name: string; size?: 'sm' | 'md' | 'lg'; className?: string;
}) {
  const sizes = {
    sm: 'h-7 w-7 text-xs',
    md: 'h-9 w-9 text-sm',
    lg: 'h-12 w-12 text-base',
  };
  // Deterministic hue per name, so the same person is always the same colour.
  const hue = useMemo(() => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
    return h;
  }, [name]);

  const letters = name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '').join('');

  return (
    <div
      className={cn('rounded-full flex items-center justify-center font-bold shrink-0', sizes[size], className)}
      style={{
        backgroundColor: `hsl(${hue} 60% 22%)`,
        color: `hsl(${hue} 85% 72%)`,
      }}
      aria-hidden
    >
      {letters}
    </div>
  );
}

/** A labelled figure — the dashboard's basic unit. */
export function Stat({ label, value, delta, tone, icon, hint, onClick }: {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  tone?: BadgeTone;
  icon?: ReactNode;
  hint?: string;
  onClick?: () => void;
}) {
  const Wrapper: any = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={cn(
        'panel text-left w-full transition-all',
        onClick && 'hover:border-faint hover:-translate-y-0.5 cursor-pointer',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="label">{label}</p>
        {icon && <div className="text-faint shrink-0">{icon}</div>}
      </div>
      <p className={cn(
        'stat-value mt-3',
        tone === 'danger' && 'text-danger',
        tone === 'ok' && 'text-ok',
        tone === 'warn' && 'text-warn',
      )}>
        {value}
      </p>
      {(delta || hint) && (
        <p className="text-xs text-faint mt-1.5">{delta ?? hint}</p>
      )}
    </Wrapper>
  );
}
